import { Note } from 'tonal';
import type { Chord } from '@/types/chord';
import type { Move, MoveContext, MoveResult } from '../types';
import { chordPitchClasses, nearestPitchClassMidi } from '../pitch-utils';

/** A minor 3rd (3 semitones) or larger counts as a leap. */
const LEAP_THRESHOLD_SEMITONES = 3;

/**
 * walk the bass — stepwise bass. Re-picks the selected chord's bass from
 * its own chord tones to approach the next chord's bass as closely as
 * possible (Tonal handles the note<->MIDI conversion per MOVES-SPEC.md).
 */
export const walkTheBass: Move = {
  id: 'walk-the-bass',
  name: 'walk the bass',
  theory: 'stepwise bass',

  applicable(ctx: MoveContext): boolean {
    const chord = ctx.chords[ctx.index];
    const next = ctx.chords[ctx.index + 1];
    if (!chord || !next) return false;
    const currentBassMidi = Note.midi(chord.voices.bass);
    const nextBassMidi = Note.midi(next.voices.bass);
    if (currentBassMidi === null || nextBassMidi === null) return false;
    return Math.abs(nextBassMidi - currentBassMidi) >= LEAP_THRESHOLD_SEMITONES;
  },

  apply(ctx: MoveContext): MoveResult {
    const chord = ctx.chords[ctx.index];
    const next = ctx.chords[ctx.index + 1];
    const nextBassMidi = Note.midi(next.voices.bass)!;
    const pitchClasses = chordPitchClasses(chord);
    const newBassMidi = nearestPitchClassMidi(pitchClasses, nextBassMidi);
    const newBass = Note.fromMidi(newBassMidi)!;

    const changed: Chord = {
      ...chord,
      voices: { ...chord.voices, bass: newBass },
      voicesCustomized: true,
      key: ctx.key,
      mode: ctx.mode,
    };
    const chords: Chord[] = ctx.chords.map((c, i) => (i === ctx.index ? changed : c));

    return {
      chords,
      changedIds: [changed.id],
      explanation: 'the bass takes the stairs instead of the elevator.',
      theoryLabel: 'stepwise bass',
    };
  },
};
